import { useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { generatePath, Link, useNavigate } from "react-router-dom";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { AlertCircle, CheckCircle2, LayoutGrid, Loader2, Plus, RefreshCw, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DataTable,
  textSortCaseInsensitive,
  type DataTableColumn,
} from "@/components/ui/DataTable/index";
import { useQueryParam } from "@/hooks/use-query-param";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import {
  useListAllCrmMasters,
  type CrmMasterSummary,
  type CrmRecordSummary,
  type CrmRecordType,
} from "@/lib/api/crm/masters";
import { RoutePath } from "../routerPaths";
import { recordTypeLabels } from "./recordTypes";
import { CreateGroupDialog } from "./components/CreateGroupDialog";
import { CrmListFilters } from "./components/CrmListFilters";
import { CrmMasterCardList } from "./components/CrmMasterCardList";
import { RecordTypeBadge } from "./components/RecordTypeBadge";
import { useCrmListViewMode } from "./useCrmListViewMode";

/** One row per record; a master without records gets a single row with `record: null`. */
type CrmListRow = {
  master_id: string;
  master_long_name: string;
  record: CrmRecordSummary | null;
  owner_long_name: string | null;
};

const MASTER_COLUMN_ID = "master";
const RECORD_NAME_COLUMN_ID = "record_long_name";
const RECORD_TYPE_COLUMN_ID = "record_type";
/** DataTable's filter value for empty cells. */
const BLANK_FILTER_VALUE = "(Blank)";
const DEFAULT_SORTING: SortingState = [
  { id: MASTER_COLUMN_ID, desc: false },
  { id: RECORD_NAME_COLUMN_ID, desc: false },
];

const labelByRecordType = new Map<string, string>(Object.entries(recordTypeLabels));
const recordTypeByLabel = new Map<string, string>(
  Object.entries(recordTypeLabels).map(([type, label]) => [label, type]),
);

/** `types` URL values (record types) -> record_type column filter values (labels). */
function toRecordTypeFilterValues(types: string[]): string[] {
  return types.flatMap((type) => {
    if (type === BLANK_FILTER_VALUE) return [type];
    const label = labelByRecordType.get(type);
    return label ? [label] : [];
  });
}

/** The table's filters with the record_type filter set to the given `types` URL values (removed when empty). */
function withRecordTypeFilter(filters: ColumnFiltersState, types: string[]): ColumnFiltersState {
  const others = filters.filter((filter) => filter.id !== RECORD_TYPE_COLUMN_ID);
  const value = toRecordTypeFilterValues(types);
  return value.length > 0 ? [...others, { id: RECORD_TYPE_COLUMN_ID, value }] : others;
}

/** record_type column filter values (labels) -> `types` URL values (record types). */
function toRecordTypeParamValues(filterValue: unknown): string[] {
  if (!Array.isArray(filterValue)) return [];
  return filterValue.flatMap((value) => {
    if (value === BLANK_FILTER_VALUE) return [value];
    const type = typeof value === "string" ? recordTypeByLabel.get(value) : undefined;
    return type ? [type] : [];
  });
}

function toRows(masters: CrmMasterSummary[]): CrmListRow[] {
  return masters.flatMap<CrmListRow>((master) => {
    if (master.records.length === 0) {
      return [{ master_id: master.id, master_long_name: master.long_name, record: null, owner_long_name: null }];
    }
    const recordNames = new Map(master.records.map((record) => [record.id, record.long_name]));
    return master.records.map((record) => ({
      master_id: master.id,
      master_long_name: master.long_name,
      record,
      owner_long_name: record.owner_id ? recordNames.get(record.owner_id) ?? null : null,
    }));
  });
}

type SearchItem = { kind: "master" | "record"; id: string; long_name: string };

/**
 * Masters matching a fuzzy long name search: a matching master keeps all its records, otherwise
 * only its matching records are kept and it's dropped when none match. Empty search keeps all.
 */
function searchMasters(masters: CrmMasterSummary[], search: string): CrmMasterSummary[] {
  const query = search.trim();
  if (query === "") return masters;
  const items = masters.flatMap<SearchItem>((master) => [
    { kind: "master", id: master.id, long_name: master.long_name },
    ...master.records.map<SearchItem>((record) => ({ kind: "record", id: record.id, long_name: record.long_name })),
  ]);
  const matches = new Fuse(items, { keys: ["long_name"], threshold: 0.3, ignoreLocation: true }).search(query);
  const masterIds = new Set(matches.filter((m) => m.item.kind === "master").map((m) => m.item.id));
  const recordIds = new Set(matches.filter((m) => m.item.kind === "record").map((m) => m.item.id));
  return masters.flatMap((master) => {
    if (masterIds.has(master.id)) return [master];
    const records = master.records.filter((record) => recordIds.has(record.id));
    return records.length > 0 ? [{ ...master, records }] : [];
  });
}

/**
 * Masters with records limited to those assigned to the given trader; masters left without
 * records are dropped. Empty trader id keeps all.
 */
function filterMastersByTrader(masters: CrmMasterSummary[], traderId: string): CrmMasterSummary[] {
  if (!traderId) return masters;
  return masters.flatMap((master) => {
    const records = master.records.filter((record) => record.trader_id === traderId);
    return records.length > 0 ? [{ ...master, records }] : [];
  });
}

/** Masters for the card view, sorted by name, with records limited to the selected `types` URL values. */
function toCardMasters(masters: CrmMasterSummary[], types: string[]): CrmMasterSummary[] {
  const filtered = types.length === 0
    ? masters
    : masters.flatMap((master) => {
      if (master.records.length === 0) return types.includes(BLANK_FILTER_VALUE) ? [master] : [];
      const records = master.records.filter((record) => types.includes(record.record_type));
      return records.length > 0 ? [{ ...master, records }] : [];
    });
  return [...filtered].sort((a, b) => a.long_name.localeCompare(b.long_name));
}

export function CrmListPage() {
  const navigate = useNavigate();
  const { mode: viewMode, toggleMode: toggleViewMode } = useCrmListViewMode();
  const [typeParam, setTypeParam] = useQueryParamArray("types");
  const [searchParam, setSearchParam] = useQueryParam("search");
  const [traderParam, setTraderParam] = useQueryParam("trader");
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const listResult = useListAllCrmMasters();

  const masters = useMemo(
    () => (listResult.data?.pages ?? []).flatMap((page) => page.items),
    [listResult.data],
  );
  const searchedMasters = useMemo(
    () => searchMasters(filterMastersByTrader(masters, traderParam), searchParam),
    [masters, traderParam, searchParam],
  );
  const masterNames = new Map(masters.map((master) => [master.id, master.long_name]));
  const rows = toRows(searchedMasters);
  const recordCount = masters.reduce((count, master) => count + master.records.length, 0);
  const isLoadingAll = listResult.isLoading || listResult.hasNextPage === true;
  // Re-fetching pages already loaded (Refresh), as opposed to loading the next page.
  const isRefreshing = listResult.isFetching && !listResult.isLoading && !listResult.isFetchingNextPage;

  // The table keeps its filters and sorting internally and only reads them when it mounts, so the
  // record type filter remounts it (new `tableKey`) with the latest filters and sorting it reported.
  const filtersRef = useRef<ColumnFiltersState>(withRecordTypeFilter([], typeParam));
  const sortingRef = useRef<SortingState>(DEFAULT_SORTING);
  const [tableKey, setTableKey] = useState(0);

  const handleFilterChange = (filters: ColumnFiltersState) => {
    filtersRef.current = filters;
    const typeFilter = filters.find((filter) => filter.id === RECORD_TYPE_COLUMN_ID);
    const nextTypes = toRecordTypeParamValues(typeFilter?.value);
    if (nextTypes.join(",") !== typeParam.join(",")) setTypeParam(nextTypes);
  };

  /** Type filter: same filter as the Type column, in memory. `null` (All) clears it. */
  const handleTypeToggle = (type: CrmRecordType | null) => {
    const nextTypes = type === null
      ? []
      : typeParam.includes(type) ? typeParam.filter((value) => value !== type) : [...typeParam, type];
    filtersRef.current = withRecordTypeFilter(filtersRef.current, nextTypes);
    setTypeParam(nextTypes);
    setTableKey((key) => key + 1);
  };

  const handleRowClick = (row: CrmListRow, columnId: string) => {
    if (columnId === MASTER_COLUMN_ID) {
      navigate(generatePath(RoutePath.CrmGroup, { masterId: row.master_id }));
    } else if (row.record) {
      navigate(generatePath(RoutePath.CrmRecordDetails, { recordId: row.record.id }));
    }
  };

  const handleRetry = () => {
    if (listResult.data) {
      void listResult.fetchNextPage();
    } else {
      void listResult.refetch();
    }
  };

  const columns: DataTableColumn<CrmListRow>[] = [
    {
      id: MASTER_COLUMN_ID,
      header: "Record Group",
      accessorFn: (row) => row.master_id,
      cell: ({ row }) => row.getIsGrouped() ? (
        <Link
          to={generatePath(RoutePath.CrmGroup, { masterId: row.original.master_id })}
          className="text-blue-700 hover:underline"
          data-testid={`crm-master-link-${row.original.master_id}`}
        >
          {row.original.master_long_name}
        </Link>
      ) : row.original.master_long_name,
      filterDisplayFn: (rawValue) => masterNames.get(String(rawValue)) ?? String(rawValue),
      sortingFn: (a, b) => a.original.master_long_name.localeCompare(b.original.master_long_name),
      size: 300,
    },
    {
      id: RECORD_NAME_COLUMN_ID,
      header: "Record",
      accessorFn: (row) => row.record?.long_name ?? null,
      cell: ({ row }) => row.original.record?.long_name ?? "",
      sortingFn: textSortCaseInsensitive,
      aggregatedCell: () => null,
      size: 280,
    },
    {
      id: RECORD_TYPE_COLUMN_ID,
      header: "Type",
      accessorFn: (row) => (row.record ? recordTypeLabels[row.record.record_type] : null),
      cell: ({ row }) => {
        const record = row.original.record;
        return record ? <RecordTypeBadge type={record.record_type} /> : "";
      },
      aggregatedCell: () => null,
      size: 160,
    },
    {
      id: "owner",
      header: "Belongs To",
      accessorFn: (row) => row.owner_long_name,
      cell: ({ row }) => row.original.owner_long_name ?? "",
      aggregatedCell: () => null,
      size: 240,
    },
  ];

  const loadedCount = recordCount.toLocaleString();

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">CRM</h1>
          <p className="text-sm text-muted-foreground">
            Every CRM record, by group. Use the column filters to search.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            title={`Switch to ${viewMode === "table" ? "card" : "table"} view`}
            data-testid="crm-list-view-mode"
          >
            {viewMode === "table" ? (
              <Table2 className="mr-2 h-4 w-4" />
            ) : (
              <LayoutGrid className="mr-2 h-4 w-4" />
            )}
            {viewMode === "table" ? "Table" : "Cards"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsCreateGroupOpen(true)} data-testid="crm-list-new-group">
            <Plus className="mr-2 h-4 w-4" />
            New record group
          </Button>
          <Button size="sm" asChild>
            <Link to={RoutePath.CrmNewRecord} data-testid="crm-list-new-record">
              <Plus className="mr-2 h-4 w-4" />
              New record
            </Link>
          </Button>
        </div>
      </div>
      {isCreateGroupOpen && <CreateGroupDialog onClose={() => setIsCreateGroupOpen(false)} />}

      <div
        className="flex min-h-[40px] flex-wrap items-center gap-3 rounded-lg border bg-white px-4 py-2 text-sm shadow-sm"
        data-testid="crm-list-status"
      >
        {listResult.isError ? (
          <>
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="text-red-700">
              Couldn't finish loading records. Showing the {loadedCount} loaded so far.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={listResult.isFetching}
              data-testid="crm-list-retry"
            >
              Try again
            </Button>
          </>
        ) : isRefreshing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-gray-700">Refreshing records…</span>
          </>
        ) : isLoadingAll ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-gray-700">Loading records… {loadedCount} so far</span>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="text-gray-700">All {loadedCount} records loaded</span>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => { void listResult.refetch(); }}
          disabled={listResult.isFetching || isRefreshing}
          data-testid="crm-list-refresh"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <CrmListFilters
        search={searchParam}
        onSearchChange={(value) => setSearchParam(value === "" ? null : value)}
        types={typeParam}
        onTypeToggle={handleTypeToggle}
        traderId={traderParam}
        onTraderChange={(value) => setTraderParam(value === "" ? null : value)}
      />

      {viewMode === "cards" ? (
        <CrmMasterCardList
          masters={toCardMasters(searchedMasters, typeParam)}
          emptyMessage={listResult.isLoading ? "Loading..." : "No records found"}
        />
      ) : (
        <DataTable
          key={tableKey}
          className="shadow-lg"
          stickyHeaders
          tableClassName="max-h-[calc(100vh-310px)] overflow-y-scroll"
          data={rows}
          columns={columns}
          enableVirtualization
          virtualizationRowHeight={49}
          virtualizationOverscan={2}
          defaultCondensed
          enableSorting
          enableMultiSort
          defaultSorting={sortingRef.current}
          onSortChange={(sorting: SortingState) => { sortingRef.current = sorting; }}
          enableFiltering
          enableGrouping
          groupBy={[MASTER_COLUMN_ID]}
          defaultDataTableGroupedToExpanded
          enableColumnResizing
          filterStyle="select"
          filterMode="internal"
          defaultFiltering={filtersRef.current}
          onFilterChange={handleFilterChange}
          rowClickable
          onRowClick={handleRowClick}
          emptyMessage={listResult.isLoading ? "Loading..." : "No records found"}
        />
      )}
    </div>
  );
}
